import { HTTPBaseService } from "@/consumers/HTTPBaseService";
import type { UserInfo, LoginModel, LoginEncriptionData, ResetPasswordDto, ForgotPasswordDto, CustomerRepresentativeDto } from "@/types/UserModel";
import type { ServiceResponse, PageResponse, BaseResponse } from "@/types/RequestResponseModels";
import type { AxiosResponse } from "axios";
import { useErrorStore } from "@/stores/error";

export class UserService extends HTTPBaseService {
  private static classInstance?: UserService;
  protected errorStore = useErrorStore();

  constructor() {
    super(import.meta.env.VITE_API_BASE);
  }

  public static getInstance(): UserService {
    if (!this.classInstance) {
      this.classInstance = new UserService();
    }

    return this.classInstance;
  }

  public listCustomerRepresentatives = async (): Promise<CustomerRepresentativeDto[]> => {
    const response = await this.instance
      .get<void, AxiosResponse<CustomerRepresentativeDto[]>>("/AppUser/customer-representatives")
      .then((result) => result.data)
      .catch(() => []);

    return Array.isArray(response) ? response : [];
  };

  public listDealerCustomerRepresentatives = async (): Promise<CustomerRepresentativeDto[]> => {
    const response = await this.instance
      .get<void, AxiosResponse<CustomerRepresentativeDto[]>>("/DealerAppUser/customer-representatives")
      .then((result) => result.data)
      .catch(() => []);

    return Array.isArray(response) ? response : [];
  };

  public login = async (loginModel: LoginModel): Promise<ServiceResponse<LoginEncriptionData>> => {
    const result = await this.instance
      .post<LoginModel, AxiosResponse<ServiceResponse<UserInfo>>>("/Authentication/loginOnWeb", loginModel)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        if (err.Value.response && err.Value.response.status == 404)
          err.Message = "Kullanıcı bulunamadı veya şifre hatalı";
        return err;
      });

    return result;
  };

  public platformLogin = async (token: string): Promise<ServiceResponse<UserInfo>> => {
    const result = await this.instance
      .post<{ token: string }, AxiosResponse<ServiceResponse<UserInfo>>>("/Authentication/platform-login", { token })
      .then((response) => response.data)
      .catch((err) => {
        if (err.Value?.response && err.Value.response.status == 404)
          err.Message = "Kullanıcı bulunamadı veya yetkiniz yok";
        return err;
      });

    return result;
  };

  public checkPolicy = async (policy: string): Promise<BaseResponse> => {
    const response = await this.instance
      .post<null, AxiosResponse<BaseResponse>>(
        "/Authentication/CheckPolicy",
        null,
        { params: { policy } },
      )
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        this.errorStore.setError({
          ErrorType: 'Error',
          ErrorText: err.Message
        });
        return Promise.reject(err);
      });

    return response;
  };

  public resetPassword = async (resetPasswordDto: ResetPasswordDto): Promise<ServiceResponse<UserInfo>> => {
    const result = await this.instance
      .post<ResetPasswordDto, AxiosResponse<ServiceResponse<UserInfo>>>("/Authentication/ResetPassword", resetPasswordDto)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        if (err.Value.response && err.Value.response.status == 404)
          err.Message = "Şifre sıfırlama hatalı";
        return err;
      });

    return result;
  };

  public forgotPassword = async (ForgotPasswordDto: ForgotPasswordDto): Promise<ServiceResponse<ForgotPasswordDto>> => {
    const result = await this.instance
      .post<ForgotPasswordDto, AxiosResponse<ServiceResponse<UserInfo>>>("/Authentication/forgotPassword", ForgotPasswordDto)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        if (err.Value.response && err.Value.response.status == 404)
          err.Message = "Şifre sıfırlama gönderim hatalı";
        return err;
      });

    return result;
    }
  }
